import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ReiknivelSection } from "../components/heimasida/ReiknivelSection";
import { useReveal, revealStyle } from "../components/heimasida/useReveal";

export const Route = createFileRoute("/verkfaeri/atomika-heimasida")({
  component: AtomikaWebsite,
});

// Opinberu vörulitirnir (public/logo/README.txt): blár #126EFD, appelsínugulur
// #FC7B1A. Navy-tónn fyrir dökka fleti. Kaflinn er sjálfstæður og snertir ekki
// sameiginlega @theme-tóna í styles.css.
const BLUE = "#126EFD";
const ORANGE = "#FC7B1A";
const NAVY = "#002B62";

const NAV_LINKS = [
  { id: "thjonusta", label: "Lausnin" },
  { id: "sparnadarmat", label: "Sparnaðarmat" },
  { id: "um", label: "Um Atomika" },
  { id: "gildi", label: "Af hverju Atomika" },
  { id: "samband", label: "Hafðu samband" },
];

const SERVICES = [
  {
    n: "01",
    title: "Rauntímavöktun hitakerfa",
    body: "Fylgstu með hita- og neysluvatnskerfum byggingarinnar í rauntíma — hitastig, flæði og notkun á einum stað, allan sólarhringinn.",
    icon: "📡",
  },
  {
    n: "02",
    title: "Frávikaviðvaranir",
    body: "Húsvörðurinn lætur strax vita þegar notkun, hitastig eða flæði fer út fyrir eðlileg mörk — svo leki, bilun eða sóun uppgötvast áður en reikningurinn hækkar.",
    icon: "🛡️",
  },
  {
    n: "03",
    title: "Sparnaðargreining",
    body: "Sjáðu nákvæmlega hvar heitt vatn og orka fara til spillis og hversu mikið má lækka hitakostnaðinn — með raunhæfum, mælanlegum aðgerðum.",
    icon: "🔋",
  },
  {
    n: "04",
    title: "Yfirsýn yfir allan flotann",
    body: "Stjórnaðu mörgum byggingum á einu mælaborði, berðu saman frammistöðu og forgangsraðaðu þangað sem ávinningurinn er mestur.",
    icon: "📊",
  },
];

const VALUES = [
  {
    n: "01",
    title: "Borgar sig strax",
    body: "Enginn stofnkostnaður og engin áhætta — við tökum aðeins hlutdeild í sparnaðinum sem næst. Sparir þú ekki, borgar þú ekki.",
  },
  {
    n: "02",
    title: "Ekkert frávik fer framhjá þér",
    body: "Sjálfvirkar viðvaranir tryggja að leki, bilun eða óeðlileg notkun uppgötvist strax — ekki þegar reikningurinn berst.",
  },
  {
    n: "03",
    title: "Einfalt í innleiðingu",
    body: "Húsvörðurinn tengist núverandi kerfum og vex með þér — frá einni byggingu upp í heilan flota, án flókinnar uppsetningar.",
  },
];

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function AtomikaWebsite() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-white text-slate-900 antialiased">
      {/* ── Nav ─────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-40 border-b border-slate-100 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
          <button
            onClick={() => scrollToId("top")}
            className="flex items-center"
            aria-label="Atomika"
          >
            <img
              src="/logo/atomika-horizontal.svg"
              alt="Atomika"
              className="h-7 w-auto"
            />
          </button>

          <div className="hidden items-center gap-8 md:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollToId(l.id)}
                className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => scrollToId("samband")}
              className="rounded-full px-5 py-2 text-sm font-semibold text-white shadow-sm transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: BLUE }}
            >
              Hafa samband
            </button>
          </div>

          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 md:hidden"
            aria-label="Valmynd"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d={
                  menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 7h16M4 12h16M4 17h16"
                }
              />
            </svg>
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-100 bg-white px-5 py-3 md:hidden">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => {
                  scrollToId(l.id);
                  setMenuOpen(false);
                }}
                className="block w-full py-2.5 text-left text-sm font-medium text-slate-700"
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* ── Hero ────────────────────────────────────────────── */}
      <header
        id="top"
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(160deg, ${NAVY} 0%, #012452 45%, #0a1a3a 100%)`,
        }}
      >
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${BLUE}55, transparent 65%)`,
          }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 left-1/4 h-80 w-80 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${ORANGE}33, transparent 65%)`,
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <h1 className="text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Stafrænn húsvörður fyrir{" "}
              <span className="bg-gradient-to-r from-blue-300 to-white bg-clip-text text-transparent">
                eignina þína
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-blue-100/80">
              Stafrænni húsvörðurinn frá Atomika stýrir og vaktar hita- og
              neysluvatnskerfi bygginga allan sólarhringinn. Með stafrænum
              húsverði hefur kostnaður við orkukaup lækkað 20% til 50%.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button
                onClick={() => scrollToId("sparnadarmat")}
                className="rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.03]"
                style={{ backgroundColor: BLUE }}
              >
                Reiknaðu notkunina þína
              </button>
              <button
                onClick={() => scrollToId("thjonusta")}
                className="rounded-full border border-white/25 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
              >
                Sjá hvernig það virkar
              </button>
            </div>
          </div>

          <div className="relative mx-auto hidden aspect-square w-full max-w-sm lg:block">
            <IoTGraphic />
          </div>
        </div>
      </header>

      {/* ── Þjónusta ────────────────────────────────────────── */}
      <section id="thjonusta" className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
        <SectionHead
          eyebrow="Stafrænn Húsvörður"
          title="Húsvörður sem aldrei sefur"
          sub="Stafrænn húsvörður frá Atomika stýrir og vaktar hita- og neysluvatnskerfi bygginga allan sólarhringinn og kostnaður við orkukaup hefur lækkað um 20% → 50%."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.n}
              delayMs={i * 60}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-shadow hover:shadow-xl"
            >
              <div
                className="absolute -right-6 -top-6 text-[6rem] font-black leading-none opacity-[0.06] transition-opacity group-hover:opacity-[0.1]"
                style={{ color: BLUE }}
              >
                {s.n}
              </div>
              <div className="relative">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl text-2xl"
                  style={{ backgroundColor: `${BLUE}12` }}
                >
                  {s.icon}
                </div>
                <h3 className="mt-5 text-lg font-bold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">
                  {s.body}
                </p>
              </div>
              <div
                className="absolute bottom-0 left-0 h-1 w-0 transition-all duration-300 group-hover:w-full"
                style={{ backgroundColor: ORANGE }}
              />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Sparnaðarmat (vatnsnýtingar-reiknivél heimasíðunnar) ─ */}
      <section
        id="sparnadarmat"
        className="relative overflow-hidden bg-slate-50 py-24"
      >
        <div
          className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${BLUE}18, transparent 65%)`,
          }}
        />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <SectionHead
            eyebrow="Sparnaðarmat"
            title="Hvað ertu að greiða fyrir heitt vatn?"
            sub="Sláðu inn notkun síðustu 12 mánaða, stærð húsnæðis og staðsetningu. Þú sérð strax vatnsnýtinguna og áætlaðan árskostnað — kostnað sem við lækkum um 20% til 50%, án stofnkostnaðar og gegn hlutdeild í sparnaðinum."
          />
          <div className="mt-12">
            <ReiknivelSection />
          </div>
        </div>
      </section>

      {/* ── Um Atomika ──────────────────────────────────────── */}
      <section id="um" className="relative overflow-hidden bg-white py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <span
              className="text-xs font-bold uppercase tracking-[0.2em]"
              style={{ color: BLUE }}
            >
              Um Atomika
            </span>
            <h2 className="mt-4 text-3xl font-black leading-tight text-slate-900 sm:text-4xl">
              Stafrænn húsvörður fyrir byggingar landsins
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Atomika þróar <strong>Stafræna Húsvörðinn</strong>, lausn sem
              stýrir og vaktar hita- og neysluvatnskerfi bygginga allan
              sólarhringinn. Við rekum örugga miðlæga þjónustu sem safnar
              mælingum og breytir þeim í skýrar aðgerðir.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Lausnin nýtist húsfélögum, fasteignarekendum, sveitarfélögum og
              veitum. Alls staðar þar sem heitt vatn og orka mega ekki fara til
              spillis.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                "Vöktun á heitu vatni og hitakerfi allan sólarhringinn",
                "Viðvaranir um leið og frávik koma upp",
                "Skýr yfirsýn yfir sparnað og frammistöðu",
              ].map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-3 text-sm text-slate-700"
                >
                  <span
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: BLUE }}
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delayMs={80}>
            <div
              className="overflow-hidden rounded-3xl p-10 shadow-2xl"
              style={{
                background: `linear-gradient(150deg, ${NAVY}, #0a1a3a)`,
              }}
            >
              <div className="flex items-center justify-center">
                <img
                  src="/logo/atomika-horizontal.svg"
                  alt="Atomika"
                  className="h-auto w-48 opacity-95 brightness-0 invert"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Gildi ───────────────────────────────────────────── */}
      <section id="gildi" className="bg-slate-50 py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <SectionHead
            eyebrow="Af hverju Atomika"
            title="Áþreifanlegur ávinningur — ekki bara tækni"
            sub="Stafræni húsvörðurinn er hannaður til að borga sig frá fyrsta degi og gera rekstur bygginga einfaldari og fyrirsjáanlegri."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal
                key={v.n}
                delayMs={i * 80}
                className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/50 p-8"
              >
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-xl text-base font-bold text-white"
                  style={{ backgroundColor: i === 1 ? ORANGE : BLUE }}
                >
                  {v.n}
                </div>
                <h3 className="mt-5 text-lg font-bold text-slate-900">
                  {v.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">
                  {v.body}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hafðu samband / CTA ─────────────────────────────── */}
      <section
        id="samband"
        className="relative overflow-hidden py-24"
        style={{ background: `linear-gradient(135deg, ${BLUE}, #0a49b8)` }}
      >
        <div
          className="pointer-events-none absolute -bottom-24 -right-16 h-80 w-80 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${ORANGE}55, transparent 65%)`,
          }}
        />
        <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <h2 className="text-3xl font-black leading-tight text-white sm:text-4xl">
              Fáðu stafrænan húsvörð í þína byggingu
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-blue-50/90">
              Við sýnum þér hvernig Stafræni Húsvörðurinn vaktar hita- og
              vatnskerfin þín, grípur frávik og lækkar kostnað — bókaðu kynningu
              eða sendu okkur línu.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <a
                href="mailto:info@atomika.ai"
                className="rounded-full bg-white px-8 py-3.5 text-sm font-bold shadow-lg transition-transform hover:scale-[1.03]"
                style={{ color: BLUE }}
              >
                Senda fyrirspurn
              </a>
              <a
                href="https://www.atomika.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-white/40 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                atomika.ai
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="bg-slate-950 py-14 text-slate-400">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 md:grid-cols-[1.5fr_1fr_1fr]">
          <div>
            <img
              src="/logo/atomika-horizontal.svg"
              alt="Atomika"
              className="h-9 w-auto brightness-0 invert"
            />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
              Stafrænn húsvörður sem vaktar hita- og vatnskerfi bygginga —
              grípur frávik og lækkar kostnað.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-500">
              Stafrænn húsvörður ehf.
              <br />
              Kt. 630726-0500
            </p>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Þjónusta
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>Rauntímavöktun</li>
              <li>Frávikaviðvaranir</li>
              <li>Sparnaðargreining</li>
              <li>Yfirsýn yfir flotann</li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Samband
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <a href="mailto:info@atomika.ai" className="hover:text-white">
                  info@atomika.ai
                </a>
              </li>
              <li>
                <a
                  href="https://www.atomika.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                >
                  www.atomika.ai
                </a>
              </li>
              <li>Reykjavík, Ísland</li>
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-12 flex max-w-6xl flex-col items-center justify-between gap-3 border-t border-white/5 px-5 pt-8 text-xs text-slate-600 sm:flex-row sm:px-8">
          <span>
            © {new Date().getFullYear()} Atomika. Allur réttur áskilinn.
          </span>
          <span>Þróað og rekið á Íslandi</span>
        </div>
      </footer>
    </div>
  );
}

function SectionHead({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub: string;
}) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <span
        className="text-xs font-bold uppercase tracking-[0.2em]"
        style={{ color: BLUE }}
      >
        {eyebrow}
      </span>
      <h2 className="mt-4 text-3xl font-black leading-tight text-slate-900 sm:text-4xl">
        {title}
      </h2>
      <p className="mt-4 text-base leading-relaxed text-slate-500">{sub}</p>
    </div>
  );
}

/** Reveal-on-scroll wrapper (kemur í stað framer-motion whileInView). */
function Reveal({
  children,
  className,
  delayMs = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delayMs?: number;
}) {
  const { ref, shown } = useReveal();
  return (
    <div ref={ref} className={className} style={revealStyle(shown, delayMs)}>
      {children}
    </div>
  );
}

/** Létt, hreyfð IoT-net myndskreyting (SVG). */
function IoTGraphic() {
  const nodes = [
    { cx: 50, cy: 50, r: 5, c: ORANGE },
    { cx: 15, cy: 22, r: 3.5, c: "#7db0ff" },
    { cx: 85, cy: 20, r: 3.5, c: "#7db0ff" },
    { cx: 20, cy: 82, r: 3.5, c: "#7db0ff" },
    { cx: 82, cy: 78, r: 3.5, c: "#7db0ff" },
    { cx: 50, cy: 10, r: 3, c: "#ffffff" },
    { cx: 92, cy: 52, r: 3, c: "#ffffff" },
    { cx: 10, cy: 52, r: 3, c: "#ffffff" },
  ];
  const edges = [1, 2, 3, 4, 5, 6, 7];
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <defs>
        <radialGradient id="pulse" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={BLUE} stopOpacity="0.35" />
          <stop offset="100%" stopColor={BLUE} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill="url(#pulse)" />
      {edges.map((i) => (
        <line
          key={i}
          x1={nodes[0].cx}
          y1={nodes[0].cy}
          x2={nodes[i].cx}
          y2={nodes[i].cy}
          stroke="#ffffff"
          strokeOpacity="0.22"
          strokeWidth="0.5"
        />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.cx} cy={n.cy} r={n.r} fill={n.c}>
            {i === 0 && (
              <animate
                attributeName="r"
                values={`${n.r};${n.r + 1.5};${n.r}`}
                dur="2.5s"
                repeatCount="indefinite"
              />
            )}
          </circle>
          {i !== 0 && (
            <circle cx={n.cx} cy={n.cy} r={n.r} fill={n.c} opacity="0.5">
              <animate
                attributeName="r"
                values={`${n.r};${n.r + 2.5};${n.r}`}
                dur={`${2 + i * 0.3}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.5;0;0.5"
                dur={`${2 + i * 0.3}s`}
                repeatCount="indefinite"
              />
            </circle>
          )}
        </g>
      ))}
    </svg>
  );
}
